<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="close">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                            <div>
                                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                    <font-awesome-icon :icon="faFileContract" class="h-6 w-6 text-blue-500" aria-hidden="true" />
                                </div>
                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                                        {{ title }}
                                    </DialogTitle>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            {{ description }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="mt-6">
                                <div class="flex items-center">
                                    <input
                                        id="terms-checkbox"
                                        type="checkbox"
                                        v-model="termsAccepted"
                                        :disabled="isProcessing"
                                        class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <label for="terms-checkbox" class="ml-2 text-sm text-gray-700">
                                        I agree to the 
                                        <a target="_blank" :href="termsUrl" class="text-blue-600 underline hover:text-blue-800">
                                            Terms & Conditions
                                        </a>
                                    </label>
                                </div>
                                <div v-if="error" class="text-red-600 text-sm mt-2">
                                    {{ error }}
                                </div>
                            </div>
                            
                            <div class="mt-5 sm:mt-6 flex gap-2">
                                <button 
                                    type="button" 
                                    class="whiteButton w-1/2" 
                                    @click="close"
                                    :disabled="isSubmitting || isProcessing"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    @click="acceptTerms"
                                    :disabled="!termsAccepted || isSubmitting || isProcessing"
                                    class="primaryButton w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {{ isProcessing ? 'Processing' : (isSubmitting ? 'Processing...' : 'Accept & Continue') }}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFileContract } from '@fortawesome/pro-light-svg-icons'
import axios from 'axios'

import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";

export default {
    components: {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
    FontAwesomeIcon
  },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        subscriptionType: {
            type: String,
            required: true
        },
        planSlug: {
            type: String,
            default: null
        },
        noFlash: {
            type: Boolean,
            default: false
        },
        isProcessing: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        title() {
            const titles = {
                'rent_payments': 'Rent Payments Terms & Conditions',
                'maintenance': 'Maintenance Terms & Conditions',
                'protect': this.getProtectTitle()
            };
            return titles[this.subscriptionType] || 'Terms & Conditions Required';
        },
        description() {
            const descriptions = {
                'rent_payments': 'Please accept the rent payments terms and conditions to continue with your subscription.',
                'maintenance': 'Please accept the maintenance terms and conditions to continue with your subscription.',
                'protect': this.getProtectDescription()
            };
            return descriptions[this.subscriptionType] || 'Please accept the terms and conditions to continue with your subscription.';
        },
        termsUrl() {
            return this.appUrl('/terms-of-service');
        }
    },
    emits: ['close', 'accept', 'success', 'error'],
    data() {
        return {
            termsAccepted: false,
            error: null,
            isSubmitting: false,
            faFileContract
        }
    },
    methods: {
        getProtectTitle() {
            // Check if planSlug indicates PROTECT PRO
            const planSlug = this.planSlug;
            if (planSlug && (
                planSlug === 'protect-pro' || 
                (typeof planSlug === 'string' && planSlug.includes('protect-pro')) ||
                planSlug === 'pro'
            )) {
                return 'RiskGuard Pro Terms & Conditions';
            }
            return 'RiskGuard Terms & Conditions';
        },
        getProtectDescription() {
            // Check if planSlug indicates PROTECT PRO
            const planSlug = this.planSlug;
            if (planSlug && (
                planSlug === 'protect-pro' || 
                (typeof planSlug === 'string' && planSlug.includes('protect-pro')) ||
                planSlug === 'pro'
            )) {
                return 'Please accept the RiskGuard Pro terms and conditions to continue with your subscription.';
            }
            return 'Please accept the RiskGuard terms and conditions to continue with your subscription.';
        },
        close() {
            // Don't reset termsAccepted if we're processing or if modal is staying open
            // Only reset when modal is actually closing (show will become false)
            // The watch on show will handle resetting when modal actually closes
            this.error = null;
            this.isSubmitting = false;
            this.$emit('close');
        },
        acceptTerms() {
            if (!this.termsAccepted) {
                this.error = 'Please accept the terms and conditions to continue.';
                return;
            }
            
            this.error = null;
            this.isSubmitting = true;
            
            // Use Inertia to submit the form
            const requestData = {
                subscription_type: this.subscriptionType
            };
            
            // If noFlash prop is true, don't show flash message (for upgrade flow)
            if (this.noFlash) {
                requestData.no_flash = true;
            }
            
            // Use axios directly rather than Inertia's router.post: Inertia v2's
            // router.post returns void (verified in @inertiajs/core source), so a
            // .catch() can't be attached, and Inertia's onError callback only fires
            // for HTTP errors (4xx/5xx) — never for network-level failures
            // (ERR_NETWORK, CORS, timeout). axios's Promise lets us catch both.
            // Compliance angle: this popup gates subscription acceptance and feeds
            // the chargeback audit trail; a silent network failure here would
            // advance the user without a server-side acceptance row.
            axios.post(route('accept-terms'), requestData)
                .then(() => {
                    this.isSubmitting = false;
                    this.error = null;
                    this.$emit('success', {
                        subscriptionType: this.subscriptionType,
                        message: 'Terms accepted successfully'
                    });
                    if (!this.isProcessing) {
                        this.close();
                    }
                })
                .catch((error) => {
                    this.isSubmitting = false;
                    if (error.response) {
                        // Server returned an error status (validation 4xx, server 5xx)
                        const data = error.response.data;
                        this.error = (data && (data.message || data.error)) || 'Failed to accept terms. Please try again.';
                    } else if (error.request) {
                        // Request was made but no response — network-level failure
                        this.error = 'Network error — please check your connection and tap Accept again.';
                    } else {
                        this.error = 'Unexpected error. Please try again.';
                    }
                    console.error('Terms acceptance error:', error);
                    this.$emit('error', {
                        subscriptionType: this.subscriptionType,
                        error: this.error
                    });
                });
        },
        
        /**
         * Reusable function that can be called from other components
         * Usage example:
         * 
         * import TermsAcceptancePopup from '@/Shared/TermsAcceptancePopup.vue';
         * 
         * // In your component's methods:
         * someMethod() {
         *   this.$refs.termsPopup.acceptTermsForSubscription('rent_payments');
         * }
         */
        acceptTermsForSubscription(subscriptionType) {
            const requestData = {
                subscription_type: subscriptionType
            };
            
            // If noFlash prop is true, don't show flash message
            if (this.noFlash) {
                requestData.no_flash = true;
            }
            
            // See note on axios vs Inertia in acceptTerms() above.
            axios.post(route('accept-terms'), requestData)
                .then(() => {
                    this.$emit('success', {
                        subscriptionType: subscriptionType,
                        message: 'Terms accepted successfully'
                    });
                })
                .catch((error) => {
                    let message;
                    if (error.response) {
                        const data = error.response.data;
                        message = (data && (data.message || data.error)) || 'Failed to accept terms. Please try again.';
                    } else if (error.request) {
                        message = 'Network error — please check your connection and tap Accept again.';
                    } else {
                        message = 'Unexpected error. Please try again.';
                    }
                    console.error('Terms acceptance error:', error);
                    this.$emit('error', {
                        subscriptionType: subscriptionType,
                        error: message
                    });
                });
        }
    },
    
    // Expose the reusable function globally for easy access
    mounted() {
        // Make the function available globally for other components to use
        if (typeof window !== 'undefined') {
            window.acceptTermsForSubscription = this.acceptTermsForSubscription;
        }
    },
    
    watch: {
        show(newVal) {
            // Only reset when modal opens (not when it closes) and not processing
            if (newVal && !this.isProcessing) {
                this.termsAccepted = false;
                this.error = null;
            }
            // When modal closes, reset state
            if (!newVal) {
                this.termsAccepted = false;
                this.error = null;
                this.isSubmitting = false;
            }
        },
        isProcessing(newVal) {
            // When processing starts, ensure checkbox stays checked
            if (newVal) {
                this.termsAccepted = true;
            }
        }
    }
}
</script>
